/**
 * Expected-gap expiry rule.
 *
 * - Date-only "YYYY-MM-DD": the gap applies through the END of that calendar day in
 *   America/Chicago, i.e. it expires at 00:00 America/Chicago the following day
 *   (DST-aware: 06:00Z in CST, 05:00Z in CDT).
 * - Full ISO 8601 timestamps expire at exactly that instant.
 *
 * Mirror of src/config/expiry.ts (backend) — keep both in sync.
 */

/** Zone in which date-only expiry dates are interpreted (NSG business time). */
export const EXPIRY_TIME_ZONE = 'America/Chicago';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Offset (ms) of `timeZone` from UTC at `instant`; negative west of Greenwich. */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const part = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second')
  );
  return wallClockAsUtc - Math.floor(instant / 1000) * 1000;
}

/** UTC instant of local midnight on year/month(1-12)/day in `timeZone` (day may overflow). */
function zonedMidnightToUtc(year: number, month: number, day: number, timeZone: string): number {
  const wallClockAsUtc = Date.UTC(year, month - 1, day);
  // Two passes settle the offset across a DST change (US changes at 02:00, never at midnight)
  let utc = wallClockAsUtc - zoneOffsetMs(wallClockAsUtc, timeZone);
  utc = wallClockAsUtc - zoneOffsetMs(utc, timeZone);
  return utc;
}

/** The instant (ms since epoch) an expiresAt value stops applying; NaN if unparseable. */
export function expiryInstant(expiresAt: string): number {
  const dateOnly = DATE_ONLY.exec(expiresAt.trim());
  if (dateOnly) {
    return zonedMidnightToUtc(
      Number(dateOnly[1]),
      Number(dateOnly[2]),
      Number(dateOnly[3]) + 1,
      EXPIRY_TIME_ZONE
    );
  }
  return Date.parse(expiresAt);
}

/** True while a gap with this expiresAt still applies at `now` (no expiry = always). */
export function isExpiryActive(expiresAt: string | undefined, now: Date = new Date()): boolean {
  if (!expiresAt) return true;
  const expiry = expiryInstant(expiresAt);
  // Unparseable values were treated as expired before; keep that (validation rejects them)
  return !Number.isNaN(expiry) && now.getTime() < expiry;
}
